// src/components/ConsentCheckbox.js
// The express renewal consent box (evergreen 3.5a): the join panel, Keep my
// membership and the Plans checkout all use it, so every renewal consent looks
// and behaves the same.
//
// Controlled: the parent owns `checked` and starts it UNTICKED; a refusal
// that needs a fresh consent (renewal-consent-required, a changed price)
// resets it in the parent. The whole row is one Pressable with the checkbox
// role, its state and the exact consent text as its label, so a screen reader
// reads what is being agreed to. On web it is focusable; Enter toggles
// through Pressable, and Space is handled here (react-native-web only fires
// presses on Space for button roles).
//
// Every button it gates carries accessibilityState={{disabled: !checked}} and,
// while unticked, <ConsentHint /> ("Tick the box above to continue.") under it.
//
// Props (pinned): { checked, onToggle, label, disabled }. onToggle is called
// with the next value.

import React from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const GOLD = '#FCD000';

export const CONSENT_HINT_TEXT = 'Tick the box above to continue.';

export function ConsentHint({ visible = true, style }) {
  if (!visible) return null;
  return <Text style={[styles.hint, style]}>{CONSENT_HINT_TEXT}</Text>;
}

export default function ConsentCheckbox({ checked, onToggle, label, disabled }) {
  const isChecked = checked === true;
  const isDisabled = disabled === true;

  const toggle = () => {
    if (isDisabled || typeof onToggle !== 'function') return;
    onToggle(!isChecked);
  };

  const onKeyDown = (e) => {
    const key = e?.key || e?.nativeEvent?.key;
    if (key !== ' ' && key !== 'Spacebar') return;
    e?.preventDefault?.(); // no page scroll
    toggle();
  };

  return (
    <Pressable
      onPress={toggle}
      disabled={isDisabled}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: isChecked, disabled: isDisabled }}
      accessibilityLabel={label}
      aria-checked={isChecked}
      {...(Platform.OS === 'web' ? { onKeyDown } : null)}
      style={[styles.row, isDisabled && styles.rowDisabled]}
    >
      <View style={[styles.box, isChecked && styles.boxChecked]}>
        {isChecked ? <Ionicons name="checkmark" size={14} color="#0b0b0b" /> : null}
      </View>
      <Text style={styles.label}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 8, marginTop: 10,
  },
  rowDisabled: { opacity: 0.5 },
  box: {
    width: 20, height: 20, borderRadius: 4, borderWidth: 2, borderColor: '#777',
    alignItems: 'center', justifyContent: 'center', marginRight: 10, marginTop: 1,
    backgroundColor: 'transparent',
  },
  boxChecked: { backgroundColor: GOLD, borderColor: GOLD },
  label: { flex: 1, color: '#ddd', fontSize: 13, lineHeight: 19 },
  hint: { color: '#888', fontSize: 12, marginTop: 6, textAlign: 'center' },
});
